/**
 * 서비스 범위(ServiceSet) — 짐 하나를 어디까지 처리하는지.
 *
 * 앱의 `room-observing.types` 에 있던 값을 여기로 옮긴다. 짐 상세 플로우를 만드는
 * 규칙이 이 패키지로 오면서, 그 규칙이 읽는 값도 같이 와야 프리뷰와 프로덕션이 같은
 * 목록을 본다. 앱은 여기서 다시 내보낸다.
 */

export const SERVICE_SET_VALUES = [
  "DISASSEMBLY_TRANSPORT_INSTALLATION",
  "DISASSEMBLY_TRANSPORT",
  "TRANSPORT_ONLY",
  "TRANSPORT_TO_FIRST_FLOOR",
  "DISPOSAL",
] as const;

export type ServiceSet = (typeof SERVICE_SET_VALUES)[number];

export const SERVICE_SET_LABELS: Record<ServiceSet, string> = {
  DISASSEMBLY_TRANSPORT_INSTALLATION: "분해/운반/설치",
  DISASSEMBLY_TRANSPORT: "분해/운반",
  TRANSPORT_ONLY: "운반만",
  TRANSPORT_TO_FIRST_FLOOR: "1층으로 운반",
  DISPOSAL: "버림",
};

// ── 개별 서비스(ServiceType) ─────────────────────────────────────────────────
//
// 서버 spec 의 SERVICE_TYPE_CONSTRAINT 효과는 조합이 아니라 **개별 서비스 TYPE 키**
// 집합으로 저장·반환한다(`allowedServiceTypeKeys`). 어드민 스펙 편집기도 이 단위를
// 그대로 편집한다 — 조합 단위로 편집하면 저장 왕복에서 선택이 넓어진다.
//
// 상담에서 실제로 고르는 단위는 조합(ServiceSet)이므로, 픽커·플로우 쪽은
// [serviceTypeKeysToServiceSets] 로 환산해서 쓴다. 환산 규칙은 서버
// `ServiceTypeConstraintResolver` 와 같다: 조합이 허용되려면 그 조합이 요구하는
// **모든** 개별 서비스가 허용 목록에 있어야 한다.

/** 서버 ServiceType enum 값 */
export const SERVICE_TYPE_VALUES = [
  "DISASSEMBLY",
  "TRANSPORT",
  "INSTALLATION",
  "TRANSPORT_TO_FIRST_FLOOR",
  "DISPOSAL",
] as const;

export type ServiceTypeKey = (typeof SERVICE_TYPE_VALUES)[number];

/** 서버 ServiceType enum 의 kor 라벨 */
export const SERVICE_TYPE_LABELS: Record<string, string> = {
  DISASSEMBLY: "분해",
  TRANSPORT: "운반",
  INSTALLATION: "설치",
  TRANSPORT_TO_FIRST_FLOOR: "1층으로 운반",
  DISPOSAL: "버림",
};

/** 조합이 요구하는 개별 서비스 (서버 ServiceSet enum 의 serviceTypes 와 동일) */
export const SERVICE_SET_REQUIRED_TYPE_KEYS: Record<ServiceSet, string[]> = {
  DISASSEMBLY_TRANSPORT_INSTALLATION: ["DISASSEMBLY", "TRANSPORT", "INSTALLATION"],
  DISASSEMBLY_TRANSPORT: ["DISASSEMBLY", "TRANSPORT"],
  TRANSPORT_ONLY: ["TRANSPORT"],
  TRANSPORT_TO_FIRST_FLOOR: ["TRANSPORT_TO_FIRST_FLOOR"],
  DISPOSAL: ["DISPOSAL"],
};

export function isServiceSet(value: string): value is ServiceSet {
  return (SERVICE_SET_VALUES as ReadonlyArray<string>).includes(value);
}

/**
 * 개별 서비스 키 배열을 조합(ServiceSet) 배열로 환산한다.
 * 반환 순서는 항상 정규 순서(SERVICE_SET_VALUES).
 */
export function serviceTypeKeysToServiceSets(typeKeys: string[]): ServiceSet[] {
  const keySet = new Set(typeKeys);
  return SERVICE_SET_VALUES.filter((set) =>
    SERVICE_SET_REQUIRED_TYPE_KEYS[set].every((t) => keySet.has(t)),
  );
}

/**
 * 조합 배열을 개별 서비스 키 배열(합집합)로 환산한다. 알 수 없는 값은 버린다.
 * 조합 단위로 저장된 과거 데이터·초안을 개별 서비스로 옮길 때만 쓴다.
 */
export function serviceSetsToServiceTypeKeys(sets: readonly string[]): ServiceTypeKey[] {
  const keys = new Set<string>();
  for (const s of sets) {
    if (!isServiceSet(s)) continue;
    for (const key of SERVICE_SET_REQUIRED_TYPE_KEYS[s]) keys.add(key);
  }
  return SERVICE_TYPE_VALUES.filter((t) => keys.has(t));
}

/** 서비스 제한 효과의 저장 표현 — 허용 목록(화이트리스트)이 정본이다 */
export interface ServiceConstraintEffectShape {
  allowedServiceSets?: string[];
  allowedServiceTypeKeys?: string[];
}

/**
 * 효과에서 허용 목록을 꺼낸다. 필드가 아예 없으면 `null` — "제한 없음"이고,
 * 빈 배열(`[]`)과는 다르다. 빈 배열은 서버에서 **아무 서비스도 허용하지 않음**을
 * 뜻하므로(`AggregatedConditionEffects`: null = 제약 없음, emptySet = 선택 불가)
 * 이 둘을 뭉개면 새 규칙이 "전부 불가"로 저장된다.
 */
function readAllowedServiceTypeKeys(
  effect: ServiceConstraintEffectShape,
): ServiceTypeKey[] | null {
  if (effect.allowedServiceTypeKeys) {
    const keys = new Set(effect.allowedServiceTypeKeys);
    return SERVICE_TYPE_VALUES.filter((t) => keys.has(t));
  }
  if (effect.allowedServiceSets?.length) {
    return serviceSetsToServiceTypeKeys(effect.allowedServiceSets);
  }
  return null;
}

/**
 * 편집기에 보여줄 **불가 서비스** 목록. 저장 표현이 허용 목록이므로 여집합이다.
 *
 * 어드민은 "이 서비스는 불가"를 고르는 게 자연스럽다 — 대부분의 규칙은 특정
 * 서비스 하나(예: 설치)만 막고 나머지는 그대로 두기 때문. 저장은 서버 표현인
 * 허용 목록으로 하고, 뒤집기는 이 함수와 [blockedToAllowedServiceTypeKeys] 에서만 한다.
 *
 * - 허용 필드 없음 → `[]` (아무것도 막지 않음)
 * - 허용이 `[]` → 전체 (아무 서비스도 허용 안 됨 = 전부 불가)
 */
export function resolveBlockedServiceTypeKeys(
  effect: ServiceConstraintEffectShape,
): ServiceTypeKey[] {
  const allowed = readAllowedServiceTypeKeys(effect);
  if (allowed === null) return [];
  const allowedSet = new Set<string>(allowed);
  return SERVICE_TYPE_VALUES.filter((t) => !allowedSet.has(t));
}

/** 불가 목록을 저장용 허용 목록(여집합)으로 되돌린다 */
export function blockedToAllowedServiceTypeKeys(blocked: readonly string[]): ServiceTypeKey[] {
  const blockedSet = new Set<string>(blocked);
  return SERVICE_TYPE_VALUES.filter((t) => !blockedSet.has(t));
}

/**
 * 저장 요청에 담을 허용 목록.
 *
 * 불가 목록을 거쳐 되돌리므로, 아직 아무것도 막지 않은 규칙은 전체 허용으로 나간다
 * (빈 배열로 나가면 서버가 "전부 불가"로 읽는다). 이미 저장된 값에 대해서는 멱등.
 */
export function toRequestAllowedServiceTypeKeys(
  effect: ServiceConstraintEffectShape,
): ServiceTypeKey[] {
  return blockedToAllowedServiceTypeKeys(resolveBlockedServiceTypeKeys(effect));
}
